import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Award, Clock, Users, TrendingUp } from 'lucide-react';

const reasons = [
  { icon: Award, title: 'Professional', desc: 'We strive to excel every day by doing better than the last day until we reach our goal — financial freedom.' },
  { icon: Users, title: 'Each Customer Is Valued', desc: 'With over eight years in Accounting and Taxation, we ensure the best possible outcome for all your obligations.' },
  { icon: Clock, title: 'Quick Turnaround', desc: 'We always consider the unique needs of each client before providing accounting and advisory services.' },
  { icon: TrendingUp, title: 'Growth Focused', desc: 'We support our clients at various stages of their business and life events, building long-term relationships.' },
];

export default function WhySection() {
  return (
    <>
      <style>{`
        .why-outer { padding: 0 24px; background: var(--white); }
        .why-section {
          display: grid; grid-template-columns: 1fr 1fr;
          min-height: 560px;
          border-radius: var(--radius-lg); overflow: hidden;
        }
        .why-left {
          background: var(--charcoal-dark);
          padding: 80px 64px; position: relative; overflow: hidden;
          display: flex; flex-direction: column; justify-content: center;
        }
        .why-left::before {
          content: '';
          position: absolute; top: 0; right: 0;
          width: 200px; height: 200px;
          background: radial-gradient(circle, rgba(201,168,76,0.12), transparent 70%);
          pointer-events: none;
        }
        .why-left-eyebrow { font-size: 11px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; color: var(--gold); margin-bottom: 16px; }
        .why-left-title {
          font-family: var(--font-display);
          font-size: clamp(26px,3vw,40px); font-weight: 700;
          color: white; line-height: 1.2; margin-bottom: 20px;
        }
        .why-left-title em { font-style: italic; color: var(--gold); }
        .why-left-body { font-size: 15px; font-weight: 300; line-height: 1.8; color: rgba(255,255,255,0.65); margin-bottom: 32px; }
        .why-checklist { display: flex; flex-direction: column; gap: 12px; margin-bottom: 36px; }
        .why-check { display: flex; align-items: flex-start; gap: 10px; font-size: 14px; color: rgba(255,255,255,0.75); }
        .why-check svg { color: var(--gold); flex-shrink: 0; margin-top: 2px; }

        .why-right { background: var(--grey-100); display: grid; grid-template-columns: 1fr 1fr; gap: 16px; padding: 16px; }
        .why-card {
          padding: 40px 32px;
          border: 1px solid var(--border);
          border-radius: var(--radius);
          background: var(--white); position: relative;
          transition: all 0.28s ease;
          overflow: hidden;
        }
        .why-card:hover { background: var(--charcoal-dark); transform: translateY(-4px); box-shadow: var(--shadow-lg); border-color: var(--charcoal-dark); }
        .why-card::after {
          content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
          background: var(--gold); transform: scaleX(0); transform-origin: left; transition: transform 0.3s ease;
        }
        .why-card:hover::after { transform: scaleX(1); }
        .wc-icon { color: var(--gold); margin-bottom: 16px; transition: transform 0.3s ease; }
        .why-card:hover .wc-icon { transform: scale(1.1); }
        .wc-title {
          font-family: var(--font-display); font-size: 17px; font-weight: 700;
          color: var(--text-dark); margin-bottom: 10px; transition: color 0.28s;
        }
        .why-card:hover .wc-title { color: white; }
        .wc-desc { font-size: 13px; font-weight: 300; line-height: 1.75; color: var(--text-light); transition: color 0.28s; }
        .why-card:hover .wc-desc { color: rgba(255,255,255,0.65); }

        @media (max-width: 900px) {
          .why-outer { padding: 0 14px; }
          .why-section { grid-template-columns: 1fr; border-radius: var(--radius); }
          .why-left { padding: 60px 32px; }
          .why-right { grid-template-columns: 1fr 1fr; }
        }
        @media (max-width: 500px) {
          .why-right { grid-template-columns: 1fr; }
        }
      `}</style>

      <section className="why-outer">
        <div className="why-section">
          <div className="why-left">
            <div className="why-left-eyebrow">Why Choose Us</div>
            <h2 className="why-left-title">
              Committed to Helping Clients <em>Manage & Grow</em> Their Business
            </h2>
            <p className="why-left-body">
              Trinity Accountants understand that every client is different, and we carefully tailor 
              our accounting and advisory services to each individual or business's own needs. 
              There is no such thing as a universal solution.
            </p>
            <div className="why-checklist">
              {['We are Reliable','Cost Effective Service','Fully Qualified Accountants','Personalised Approach','Quick Turnaround'].map(item => (
                <div key={item} className="why-check">
                  <CheckCircle2 size={17} /> {item}
                </div>
              ))}
            </div>
            <Link to="/why-choose-us" className="btn-primary" style={{alignSelf:'flex-start'}}>
              Learn More <ArrowRight size={15} />
            </Link>
          </div>

          <div className="why-right">
            {reasons.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="why-card">
                <Icon size={28} className="wc-icon" />
                <div className="wc-title">{title}</div>
                <div className="wc-desc">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
